"use client";

import { useEffect, useState, useRef } from "react";
import { useUser } from "@auth0/nextjs-auth0/client";
import { useSearchParams } from "next/navigation";
import { toast } from "react-toastify";
import { motion } from "motion/react";
import { Loader, User, UserCheck, Clock, Shield, X, AlertTriangle, Info, CalendarDays, SendHorizonal, Building } from "lucide-react";
import { useOrganizationInvitation } from "@/lib/orchestrator/organizations/useOrganizationInvitation";
import { useWorkspace } from "@/providers/WorkspaceContext";
import Image from "next/image";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import localizedFormat from "dayjs/plugin/localizedFormat";

// Initialize dayjs plugins
dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);

// Type definition for invite details from verification endpoint
interface InviteDetails {
  organization_name: string;
  organization_logo: string | null;
  inviter_name: string;
  role: string;
  email: string;
  expires_at: string;
}

// More detailed type from the full invitation data
interface DetailedInviteData {
  id: string;
  token: string;
  email: string;
  role: string;
  role_display: string;
  status: string;
  status_display: string;
  invited_by: string;
  invited_by_name: string;
  invited_by_email: string;
  organization: string;
  organization_name: string;
  organization_logo_url: string;
  created_at: string;
  updated_at: string;
  expires_at: string;
  days_until_expiry: number;
  accepted_at: string | null;
  accepted_by: string | null;
  accepted_by_name: string | null;
  declined_at: string | null;
  declined_by_name: string | null;
  send_count: number;
  last_sent_at: string | null;
  metadata: Record<string, any> | null;
  is_expired: boolean;
  frontend_url: string;
}

interface InviteHandlerProps {
  onClose?: () => void;
}

export default function InviteHandler({ onClose }: InviteHandlerProps) {
  const { user, isLoading: isUserLoading } = useUser();
  const searchParams = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [inviteDetails, setInviteDetails] = useState<InviteDetails | null>(null);
  const [detailedData, setDetailedData] = useState<DetailedInviteData | null>(null);
  const { acceptInvitation, declineInvitation, verifyInvitationToken } = useOrganizationInvitation();
  const { getWorkspaces } = useWorkspace();
  const detailsLoadedRef = useRef(false);
  const [showDeclineConfirm, setShowDeclineConfirm] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);

  const inviteToken = searchParams.get("invite");

  // Format the expiration date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return dayjs(dateString).format("MMM D, YYYY");
  };

  // Format relative time
  const formatRelativeTime = (dateString: string | null) => {
    if (!dateString) return "";
    return dayjs(dateString).fromNow();
  };

  // Check if invitation has expired
  const isExpired = () => {
    if (detailedData?.is_expired) return true;
    if (!inviteDetails?.expires_at) return false;

    const expiryDate = new Date(inviteDetails.expires_at);
    return expiryDate < new Date();
  };

  // Function to filter metadata to only show relevant fields
  const getRelevantMetadata = (metadata: Record<string, any>) => {
    // List of fields to exclude - internal fields or ones that shouldn't be shown to users
    const excludedFields = [
      'declined_by', 'declined_at', 'previous_declined_by', 'previous_declined_at',
      'internal_notes', 'system_data', 'tracking_id'
    ];

    // Return filtered metadata
    return Object.fromEntries(
      Object.entries(metadata).filter(([key]) => !excludedFields.includes(key))
    );
  };

  // Show only if we have a token and user
  useEffect(() => {
    if (inviteToken && user && !isUserLoading && !isComplete) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [inviteToken, user, isUserLoading, isComplete]);

  // Load invitation details
  useEffect(() => {
    const loadInviteDetails = async () => {
      if (!inviteToken || !user || detailsLoadedRef.current || !isVisible) return;

      setIsLoadingDetails(true);
      detailsLoadedRef.current = true;

      try {
        const response = await verifyInvitationToken(inviteToken);
        if (response && response.data) {
          if (response.data.id) {
            // This is the detailed data response
            setDetailedData(response.data as unknown as DetailedInviteData);

            // Also set the basic invite details for compatibility
            setInviteDetails({
              organization_name: response.data.organization_name,
              organization_logo: response.data.organization_logo_url,
              inviter_name: response.data.invited_by_name,
              role: response.data.role,
              email: response.data.email,
              expires_at: response.data.expires_at
            });
          } else {
            // This is the basic verification response
            setInviteDetails(response.data);
          }
        }
      } catch (error) {
        console.error("Failed to load invitation details:", error);
        toast.error("Failed to load invitation details");
      } finally {
        setIsLoadingDetails(false);
      }
    };

    loadInviteDetails();
  }, [inviteToken, user, verifyInvitationToken, isVisible]);

  // Process invitation acceptance
  const handleAcceptInvite = async () => {
    if (!inviteToken || !user || isProcessing) return;

    setIsProcessing(true);

    try {
      const response = await acceptInvitation(inviteToken);

      if (response && response.data) {
        toast.success(`Successfully joined ${response.data.organization_name}`);

        // Refresh workspaces to include the newly joined workspace
        await getWorkspaces();

        // Remove invite parameter from URL without triggering a reload
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete("invite");
        window.history.replaceState({}, '', newUrl.toString());

        // Complete the process
        setIsComplete(true);
        setIsVisible(false);
      }
    } catch (error: any) {
      console.error("[InviteHandler] Failed to accept invitation:", error);

      // Handle specific error cases based on the API response
      if (error?.response?.status === 401) {
        // Authentication issue - redirect to login
        const loginUrl = `/api/auth/login?invite=${inviteToken}`;
        window.location.href = loginUrl;
        return;
      } else if (error?.response?.status === 400) {
        const errorMessage = error.response?.data?.message;
        if (errorMessage?.includes("already been accepted")) {
          toast.info("You've already joined this organization");
        } else if (errorMessage?.includes("expired")) {
          toast.error("This invitation has expired");
        } else if (errorMessage?.includes("different email")) {
          toast.error("This invitation was sent to a different email address");
        } else {
          toast.error(errorMessage || "Invalid invitation");
        }
      } else if (error?.response?.status === 404) {
        toast.error("Invalid invitation token");
      } else {
        toast.error("Failed to accept invitation. Please try again.");
      }

      // Remove invite parameter from URL
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete("invite");
      window.history.replaceState({}, '', newUrl.toString());

      // Still mark as complete
      setIsComplete(true);
      setIsVisible(false);
    } finally {
      setIsProcessing(false);
    }
  };

  // Process invitation decline
  const handleDeclineInvite = async () => {
    if (!inviteToken || !user || isDeclining) return;

    setIsDeclining(true);

    try {
      const response = await declineInvitation(inviteToken);

      if (response && response.data) {
        toast.info(`You've declined the invitation to join ${response.data.organization_name}`);

        // Remove invite parameter from URL without triggering a reload
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete("invite");
        window.history.replaceState({}, '', newUrl.toString());

        // Complete the process
        setIsComplete(true);
        setIsVisible(false);
      }
    } catch (error: any) {
      console.error("[InviteHandler] Failed to decline invitation:", error);

      // Handle specific error cases based on the API response
      if (error?.response?.status === 401) {
        // Authentication issue - just close
        toast.error("Authentication required to decline invitation");
      } else if (error?.response?.status === 400) {
        const errorMessage = error.response?.data?.message;
        if (errorMessage?.includes("already been declined")) {
          toast.info("You've already declined this invitation");
        } else if (errorMessage?.includes("expired")) {
          toast.info("This invitation has already expired");
        } else {
          toast.error(errorMessage || "Unable to decline invitation");
        }
      } else {
        toast.error("Failed to decline invitation");
      }

      // Remove invite parameter from URL
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete("invite");
      window.history.replaceState({}, '', newUrl.toString());

      // Still mark as complete
      setIsComplete(true);
      setIsVisible(false);
    } finally {
      setIsDeclining(false);
      setShowDeclineConfirm(false);
    }
  };

  // Handle cancellation or closure
  const handleCancel = () => {
    // Remove invite parameter from URL
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.delete("invite");
    window.history.replaceState({}, '', newUrl.toString());

    // Close the invite handler
    setIsComplete(true);
    setIsVisible(false);

    // Call the onClose callback if provided
    if (onClose) {
      onClose();
    }
  };

  // Check if email matches the invitation
  const checkEmailMismatch = () => {
    if (!inviteDetails || !user?.email) return false;
    return inviteDetails.email.toLowerCase() !== user.email.toLowerCase();
  };

  const hasEmailMismatch = checkEmailMismatch();
  const expired = isExpired();
  const alreadyAccepted = detailedData?.status === "accepted";
  const alreadyDeclined = detailedData?.status === "declined";

  // Extract metadata fields if available
  const metadata = detailedData?.metadata || {};
  const relevantMetadata = getRelevantMetadata(metadata);
  const hasMetadata = Object.keys(relevantMetadata).length > 0;

  if (!isVisible) {
    return null;
  }

  // Fix organization logo URL if needed
  const logoUrl = detailedData?.organization_logo_url || inviteDetails?.organization_logo;
  const fixedLogoUrl = logoUrl && !logoUrl.startsWith('http') && !logoUrl.startsWith('data:')
    ? `${process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000'}${logoUrl}`
    : logoUrl;

  // Status indicator for display
  const getStatusIndicator = () => {
    if (alreadyAccepted) return { class: "bg-green-900/60 text-green-200", text: "Already Joined" };
    if (alreadyDeclined) return { class: "bg-yellow-900/60 text-yellow-200", text: "Declined" };
    if (expired) return { class: "bg-red-900/60 text-red-200", text: "Expired" };
    return null;
  };

  const statusIndicator = getStatusIndicator();

  // Determine warning banners to show
  const shouldShowEmailMismatchWarning = hasEmailMismatch;
  const shouldShowAcceptedWarning = alreadyAccepted;
  const shouldShowDeclinedWarning = alreadyDeclined;
  const shouldShowExpiredWarning = expired && !alreadyAccepted && !alreadyDeclined;

  // If we're showing the decline confirmation dialog
  if (showDeclineConfirm) {
    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[60]">
        <div className="bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
          <h2 className="text-xl font-bold text-white mb-3">Decline Invitation</h2>
          <p className="text-gray-300 mb-4">
            Are you sure you want to decline the invitation to join <span className="font-semibold text-white">{detailedData?.organization_name || inviteDetails?.organization_name}</span>?
          </p>
          <p className="text-amber-300 text-sm mb-5">
            <AlertTriangle className="inline-block w-4 h-4 mr-1" />
            If you decline, the organization admin will be notified. You can always be invited again later.
          </p>

          <div className="flex gap-3">
            <button
              onClick={() => setShowDeclineConfirm(false)}
              className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-md text-white transition-colors"
              disabled={isDeclining}
            >
              Cancel
            </button>
            <button
              onClick={handleDeclineInvite}
              className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-500 rounded-md text-white transition-colors flex items-center justify-center"
              disabled={isDeclining}
            >
              {isDeclining ? (
                <>
                  <Loader className="w-4 h-4 animate-spin mr-2" />
                  Declining...
                </>
              ) : (
                "Decline Invitation"
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[60]"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-gray-800/90 backdrop-blur-lg rounded-xl overflow-hidden max-w-4xl w-full mx-4 shadow-2xl max-h-[85vh] flex flex-col"
      >
        {/* Close Button */}
        <button
          onClick={handleCancel}
          className="absolute top-4 right-4 p-1 rounded-full bg-gray-700/50 hover:bg-gray-600/50 transition-colors z-10"
        >
          <X className="w-5 h-5 text-gray-300" />
        </button>

        {/* Loading State */}
        {isLoadingDetails ? (
          <div className="p-8 flex flex-col items-center">
            <Loader className="w-10 h-10 text-blue-400 animate-spin mb-4" />
            <h2 className="text-xl font-medium text-white mb-2">Loading invitation details...</h2>
            <p className="text-gray-400 text-center">Verifying invitation information</p>
          </div>
        ) : (
          <>
            {/* Header with Organization Logo */}
            <div className="relative overflow-hidden w-full h-20 bg-linear-to-r from-blue-900/70 to-purple-900/70 flex items-center px-6">
              <div className="absolute inset-0 bg-linear-to-r from-blue-500/20 to-purple-500/20"></div>

              {/* Logo */}
              <div className="w-12 h-12 rounded-lg overflow-hidden border-2 border-gray-800/90 bg-gray-700 flex items-center justify-center mr-4 relative z-10">
                {fixedLogoUrl ? (
                  <Image
                    src={fixedLogoUrl}
                    width={48}
                    height={48}
                    alt={(detailedData?.organization_name || inviteDetails?.organization_name || "Organization") + " logo"}
                    className="object-cover w-full h-full"
                  />
                ) : (
                  <span className="text-lg font-bold text-white">
                    {(detailedData?.organization_name || inviteDetails?.organization_name || "O").charAt(0)}
                  </span>
                )}
              </div>

              {/* Organization Name */}
              <div className="flex-1 relative z-10">
                <h1 className="text-xl font-bold text-white">
                  {detailedData?.organization_name || inviteDetails?.organization_name || "Organization"}
                </h1>
                <p className="text-sm text-gray-300">
                  Invitation to join as {detailedData?.role_display ||
                    (inviteDetails?.role ? inviteDetails.role.charAt(0).toUpperCase() + inviteDetails.role.slice(1) : "Member")}
                </p>
              </div>

              {/* Status badge */}
              {statusIndicator && (
                <div className="relative z-10">
                  <div className={`px-3 py-1 rounded-full text-xs font-medium ${statusIndicator.class}`}>
                    {statusIndicator.text}
                  </div>
                </div>
              )}
            </div>

            {/* Content Area - Scrollable */}
            <div className="flex-1 overflow-y-auto p-5">
              <div className="md:flex md:gap-6">
                {/* Left Column - Details */}
                <div className="md:w-7/12">
                  <h2 className="text-xl font-bold text-white mb-4 md:hidden">
                    Organization Invitation
                  </h2>

                  {/* Warning Banners - Only on Mobile */}
                  <div className="md:hidden space-y-3 mb-4">
                    {shouldShowEmailMismatchWarning && (
                      <div className="bg-amber-900/30 border border-amber-500/30 rounded-lg p-3 flex items-start">
                        <AlertTriangle className="w-5 h-5 text-amber-400 mr-2 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-amber-200 font-medium">Email Mismatch</p>
                          <p className="text-amber-200/80 text-sm">
                            This invitation was sent to <span className="font-medium">{inviteDetails?.email}</span>, but you're
                            logged in as <span className="font-medium">{user?.email}</span>.
                          </p>
                        </div>
                      </div>
                    )}

                    {shouldShowAcceptedWarning && (
                      <div className="bg-green-900/30 border border-green-500/30 rounded-lg p-3 flex items-start">
                        <Info className="w-5 h-5 text-green-400 mr-2 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-green-200 font-medium">Already Joined</p>
                          <p className="text-green-200/80 text-sm">
                            You've already accepted this invitation.
                          </p>
                        </div>
                      </div>
                    )}

                    {shouldShowDeclinedWarning && (
                      <div className="bg-yellow-900/30 border border-yellow-500/30 rounded-lg p-3 flex items-start">
                        <Info className="w-5 h-5 text-yellow-400 mr-2 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-yellow-200 font-medium">Invitation Declined</p>
                          <p className="text-yellow-200/80 text-sm">
                            You've already declined this invitation. Contact the organization admin if you've changed your mind.
                          </p>
                        </div>
                      </div>
                    )}

                    {shouldShowExpiredWarning && (
                      <div className="bg-red-900/30 border border-red-500/30 rounded-lg p-3 flex items-start">
                        <AlertTriangle className="w-5 h-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-red-200 font-medium">Invitation Expired</p>
                          <p className="text-red-200/80 text-sm">
                            This invitation has expired. Please contact the organization administrator.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Invitation Details */}
                  <div className="bg-gray-700/30 rounded-lg p-4 mb-5">
                    <h3 className="text-white font-medium text-sm uppercase tracking-wider mb-3">Invitation Details</h3>
                    <div className="grid grid-cols-1 gap-3">
                      {/* Invited by */}
                      <div className="flex items-center">
                        <User className="w-5 h-5 text-blue-400 mr-3 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-gray-400">Invited by</p>
                          <p className="text-white text-sm">
                            {detailedData?.invited_by_name || inviteDetails?.inviter_name || "Organization Administrator"}
                            {detailedData?.invited_by_email && detailedData.invited_by_email !== detailedData?.invited_by_name && (
                              <span className="text-xs text-gray-400 ml-1">({detailedData.invited_by_email})</span>
                            )}
                          </p>
                        </div>
                      </div>

                      {/* Invited as */}
                      <div className="flex items-center">
                        <Shield className="w-5 h-5 text-blue-400 mr-3 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-gray-400">Your role will be</p>
                          <p className="text-white text-sm">
                            {detailedData?.role_display ||
                              (inviteDetails?.role ?
                                inviteDetails.role.charAt(0).toUpperCase() + inviteDetails.role.slice(1) :
                                "Member"
                              )
                            }
                          </p>
                        </div>
                      </div>

                      {/* Email */}
                      <div className="flex items-center">
                        <UserCheck className="w-5 h-5 text-blue-400 mr-3 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-gray-400">Invitation sent to</p>
                          <p className="text-white text-sm">{detailedData?.email || inviteDetails?.email || user?.email}</p>
                        </div>
                      </div>

                      {/* Invitation Sent */}
                      <div className="flex items-center">
                        <SendHorizonal className="w-5 h-5 text-blue-400 mr-3 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-gray-400">Sent on</p>
                          <p className="text-white text-sm">
                            {formatDate(detailedData?.created_at)}
                            {detailedData?.send_count && detailedData.send_count > 1 && (
                              <span className="text-gray-400 text-xs ml-1">
                                (sent {detailedData.send_count} times)
                              </span>
                            )}
                            {detailedData?.created_at && (
                              <span className="text-gray-400 text-xs ml-1">
                                ({formatRelativeTime(detailedData.created_at)})
                              </span>
                            )}
                          </p>
                        </div>
                      </div>

                      {/* Expiration */}
                      <div className="flex items-center">
                        <CalendarDays className="w-5 h-5 text-blue-400 mr-3 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-gray-400">Expires on</p>
                          <p className={`${expired ? "text-red-300" : "text-white"} text-sm`}>
                            {formatDate(detailedData?.expires_at || inviteDetails?.expires_at)}
                            {!expired && detailedData?.expires_at && (
                              <span className="text-gray-400 text-xs ml-1">
                                ({formatRelativeTime(detailedData.expires_at)})
                              </span>
                            )}
                          </p>
                        </div>
                      </div>

                      {/* Accepted information if available */}
                      {alreadyAccepted && detailedData?.accepted_at && (
                        <div className="flex items-center">
                          <Clock className="w-5 h-5 text-green-400 mr-3 flex-shrink-0" />
                          <div>
                            <p className="text-xs text-gray-400">Accepted on</p>
                            <p className="text-green-300 text-sm">
                              {formatDate(detailedData.accepted_at)}
                              <span className="text-gray-400 text-xs ml-1">
                                ({formatRelativeTime(detailedData.accepted_at)})
                                {detailedData.accepted_by_name && ` by ${detailedData.accepted_by_name}`}
                              </span>
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Declined information if available */}
                      {alreadyDeclined && detailedData?.declined_at && (
                        <div className="flex items-center">
                          <Clock className="w-5 h-5 text-yellow-400 mr-3 flex-shrink-0" />
                          <div>
                            <p className="text-xs text-gray-400">Declined on</p>
                            <p className="text-yellow-300 text-sm">
                              {formatDate(detailedData.declined_at)}
                              <span className="text-gray-400 text-xs ml-1">
                                ({formatRelativeTime(detailedData.declined_at)})
                                {detailedData.declined_by_name && ` by ${detailedData.declined_by_name}`}
                              </span>
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Organization */}
                      <div className="flex items-center">
                        <Building className="w-5 h-5 text-blue-400 mr-3 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-gray-400">Organization</p>
                          <p className="text-white text-sm font-medium">
                            {detailedData?.organization_name || inviteDetails?.organization_name || "Organization"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Personal Information from Metadata - Mobile Only */}
                  {hasMetadata && (
                    <div className="bg-indigo-900/30 border border-indigo-500/30 rounded-lg p-3 mb-4 md:hidden">
                      <h3 className="text-indigo-200 font-medium text-sm mb-2">Your Information</h3>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                        {Object.entries(relevantMetadata).map(([key, value]) => (
                          <div key={key} className="text-sm">
                            <span className="text-indigo-300/80 text-xs">
                              {key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')}:
                            </span>
                            <span className="text-white text-sm ml-1 block">
                              {typeof value === 'string' &&
                               (key.includes('date') || key.includes('time')) ?
                               dayjs(value).format('MMM D, YYYY') :
                               value?.toString()}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Right Column - Additional Info & Actions */}
                <div className="md:w-5/12 md:pt-0 pt-2">
                  {/* Warning Banners - Desktop Only */}
                  <div className="hidden md:block space-y-3 mb-4">
                    {shouldShowEmailMismatchWarning && (
                      <div className="bg-amber-900/30 border border-amber-500/30 rounded-lg p-3 flex items-start">
                        <AlertTriangle className="w-5 h-5 text-amber-400 mr-2 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-amber-200 font-medium">Email Mismatch</p>
                          <p className="text-amber-200/80 text-sm">
                            This invitation was sent to <span className="font-medium">{inviteDetails?.email}</span>, but you're
                            logged in as <span className="font-medium">{user?.email}</span>.
                          </p>
                        </div>
                        </div>
                    )}

                    {shouldShowAcceptedWarning && (
                      <div className="bg-green-900/30 border border-green-500/30 rounded-lg p-3 flex items-start">
                        <Info className="w-5 h-5 text-green-400 mr-2 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-green-200 font-medium">Already Joined</p>
                          <p className="text-green-200/80 text-sm">
                            You've already accepted this invitation on {formatDate(detailedData?.accepted_at)}.
                            You can close this dialog and select the workspace from your list.
                          </p>
                        </div>
                      </div>
                    )}

                    {shouldShowDeclinedWarning && (
                      <div className="bg-yellow-900/30 border border-yellow-500/30 rounded-lg p-3 flex items-start">
                        <Info className="w-5 h-5 text-yellow-400 mr-2 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-yellow-200 font-medium">Invitation Declined</p>
                          <p className="text-yellow-200/80 text-sm">
                            You've already declined this invitation on {formatDate(detailedData?.declined_at)}.
                            Contact the organization administrator if you've changed your mind.
                          </p>
                        </div>
                      </div>
                    )}

                    {shouldShowExpiredWarning && (
                      <div className="bg-red-900/30 border border-red-500/30 rounded-lg p-3 flex items-start">
                        <AlertTriangle className="w-5 h-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-red-200 font-medium">Invitation Expired</p>
                          <p className="text-red-200/80 text-sm">
                            This invitation expired on {formatDate(detailedData?.expires_at || inviteDetails?.expires_at)}.
                            Please contact the organization administrator for a new invitation.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Personal Information from Metadata - Desktop Only */}
                  {hasMetadata && (
                    <div className="bg-indigo-900/30 border border-indigo-500/30 rounded-lg p-4 mb-4 hidden md:block">
                      <h3 className="text-white font-medium text-sm uppercase tracking-wider mb-2">Your Information</h3>
                      <div className="grid grid-cols-1 gap-y-2">
                        {Object.entries(relevantMetadata).map(([key, value]) => (
                          <div key={key} className="flex items-center">
                            <span className="text-indigo-300/80 text-xs w-24">
                              {key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')}:
                            </span>
                            <span className="text-white text-sm ml-2">
                              {typeof value === 'string' &&
                               (key.includes('date') || key.includes('time')) ?
                               dayjs(value).format('MMM D, YYYY') :
                               value?.toString()}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Security Note */}
                  <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3 mb-4">
                    <h3 className="text-white font-medium text-sm uppercase tracking-wider flex items-center mb-1">
                      <Info className="w-4 h-4 text-blue-400 mr-2" />
                      Information
                    </h3>
                    <p className="text-gray-300 text-sm">
                      {alreadyAccepted
                        ? "You've already joined this organization. You can return to the workspace selection screen to access it."
                        : alreadyDeclined
                        ? "You've already declined this invitation. Contact the organization administrator if you've changed your mind."
                        : "If this invitation seems incorrect or you weren't expecting it, please decline it. Only accept invitations from organizations you recognize and trust."}
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-3">
                    {alreadyAccepted || alreadyDeclined ? (
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={handleCancel}
                        className="w-full px-4 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition-colors"
                      >
                        Close
                      </motion.button>
                    ) : (
                      <>
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => setShowDeclineConfirm(true)}
                          className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition-colors"
                          disabled={isProcessing || expired}
                        >
                          Decline
                        </motion.button>

                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={handleAcceptInvite}
                          disabled={isProcessing || expired}
                          className={`flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-500 rounded-lg text-white transition-colors ${
                            (isProcessing || expired) ? 'opacity-70 cursor-not-allowed' : ''
                          }`}
                        >
                          {isProcessing ? (
                            <div className="flex items-center justify-center">
                              <Loader className="w-5 h-5 animate-spin mr-2" />
                              <span>Processing...</span>
                            </div>
                          ) : (
                            "Accept Invitation"
                          )}
                        </motion.button>
                      </>
                    )}
                  </div>

                  {/* Note about workspace selection */}
                  <p className="text-center text-gray-400 text-sm mt-3">
                    {alreadyAccepted
                      ? "This workspace is already available in your workspace list."
                      : alreadyDeclined
                      ? "Contact the organization administrator for a new invitation if needed."
                      : "After accepting, you'll be able to select this workspace from your list."}
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}